import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { getSdkAuthStore } from '../../auth/state/auth-state.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import {
  type NormalizedEntityWriteMeta,
  type NormalizedEntityWriteResult,
  writeNormalizedEntitySnapshot,
} from './normalized-entity-write.js';

const PUBLIC_USER_FIELDS = [
  'userId',
  'pubkey',
  'walletAddress',
  'displayName',
  'handle',
  'avatarUrl',
  'avatarNsfw',
  'avatarPreferences',
  'heroUrl',
  'heroNsfw',
  'bio',
  'country',
] as const;

const PRIVATE_OR_SCOPED_USER_FIELDS = [
  'email',
  'emailVerified',
  'phoneNumber',
  'turnkeySubOrgId',
  'onboardingCompleted',
  'locationSource',
  'totalPnl',
  'realizedPnl',
  'unrealizedPnl',
  'totalVolume',
  'totalTrades',
  'connectedProviders',
  'wallets',
] as const;

export type PublicUserEntity = Partial<Record<(typeof PUBLIC_USER_FIELDS)[number], unknown>>;

export interface PublicUserWriteOptions extends NormalizedEntityWriteMeta {
  readonly aliases?: readonly string[];
}

export interface PublicUserWriteResult extends NormalizedEntityWriteResult {
  readonly entityId: string | null;
}

export interface PublicUserDiagnostic {
  readonly kind: 'alias-collision' | 'private-boundary' | 'stale-write' | 'auth-reconciliation';
  readonly entityHash: string;
  readonly aliasHash?: string;
}

export function publicUserEntityKey(entityId: string): QueryKey {
  return ['users', 'entity', entityId];
}

export function publicUserAliasKey(alias: string): QueryKey {
  return ['users', 'alias', normalizeAlias(alias)];
}

export function reconcilePublicUserAvatarModeration(
  queryClient: QueryClient,
  input: { userId: string; avatarUrl: string; avatarNsfw: boolean },
): void {
  queryClient.setQueryData<PublicUserEntity>(publicUserEntityKey(input.userId), (previous) => {
    if (previous?.avatarUrl !== input.avatarUrl || previous.avatarNsfw === input.avatarNsfw) {
      return previous;
    }
    return { ...previous, avatarNsfw: input.avatarNsfw };
  });

  const auth = getSdkAuthStore().current().user;
  if (
    auth?.id === input.userId &&
    auth.avatarUrl === input.avatarUrl &&
    auth.avatarNsfw !== input.avatarNsfw
  ) {
    getSdkAuthStore().setUser({ ...auth, avatarNsfw: input.avatarNsfw });
  }
}

export function resolvePublicUserEntityId(
  queryClient: QueryClient,
  alias: string,
): string | undefined {
  const normalized = normalizeAlias(alias);
  if (normalized.length === 0) return undefined;
  return queryClient.getQueryData<string>(publicUserAliasKey(normalized));
}

export function upsertPublicUserEntity(
  queryClient: QueryClient,
  input: Record<string, unknown>,
  options: PublicUserWriteOptions,
): PublicUserWriteResult {
  const entity = publicUserSnapshot(input);
  const aliases = publicUserAliases(input, options.aliases);
  const explicitStableId = readNonEmptyString(input.userId);
  const stableId =
    explicitStableId !== undefined && isUserUuid(explicitStableId)
      ? explicitStableId
      : aliases.find(isUserUuid);
  const existingIds = new Set(
    aliases
      .map((alias) => resolvePublicUserEntityId(queryClient, alias))
      .filter((id): id is string => id !== undefined),
  );
  const entityId = stableId ?? existingIds.values().next().value ?? aliases[0] ?? null;
  if (entityId === null) {
    recordDiagnostic({ kind: 'private-boundary', entityHash: hashDiagnosticEntity('missing') });
    return { status: 'stale', entityId };
  }
  if (PRIVATE_OR_SCOPED_USER_FIELDS.some((field) => input[field] !== undefined)) {
    recordDiagnostic({
      kind: 'private-boundary',
      entityHash: hashDiagnosticEntity(entityId),
    });
  }

  let merged: PublicUserEntity = {};
  for (const previousId of existingIds) {
    const previous = queryClient.getQueryData<PublicUserEntity>(publicUserEntityKey(previousId));
    if (previous !== undefined) merged = { ...merged, ...previous };
    if (previousId !== entityId) {
      recordDiagnostic({
        kind: 'alias-collision',
        entityHash: hashDiagnosticEntity(entityId),
        aliasHash: hashDiagnosticEntity(previousId),
      });
      repointAliases(queryClient, previousId, entityId);
      queryClient.removeQueries({ queryKey: publicUserEntityKey(previousId), exact: true });
    }
  }

  const entityKey = publicUserEntityKey(entityId);
  const nextSnapshot = {
    ...merged,
    ...entity,
    // A wallet-only leaf may carry null for an unknown UUID. Once its alias
    // resolves to a UUID, that partial identity cannot erase the canonical id.
    ...(isUserUuid(entityId) ? { userId: entityId } : {}),
  };
  // Public-user entity observers are subscriber-only with infinite stale time;
  // REST query freshness lives on the source query. Advance the race fence for
  // every observation, but do not notify every avatar/name subscriber when the
  // canonical identity payload is structurally unchanged.
  const result = writeNormalizedEntitySnapshot(queryClient, entityKey, nextSnapshot, options, {
    skipEqual: true,
  });
  if (result.status === 'stale') {
    recordDiagnostic({ kind: 'stale-write', entityHash: hashDiagnosticEntity(entityId) });
  }
  for (const alias of aliases) setPublicUserAlias(queryClient, alias, entityId);
  setPublicUserAlias(queryClient, entityId, entityId);
  return { ...result, entityId };
}

export function materializePublicUserIdentity<T extends Record<string, unknown>>(
  embedded: T,
  canonical: PublicUserEntity | undefined,
): T {
  if (canonical === undefined) return embedded;
  return { ...embedded, ...canonical } as T;
}

export function stripPublicUserIdentityFields<T extends Record<string, unknown>>(row: T): T {
  if (!PUBLIC_USER_FIELDS.some((field) => Object.hasOwn(row, field))) return row;
  const projection = { ...row };
  for (const field of PUBLIC_USER_FIELDS) delete projection[field];
  return projection;
}

export function publicUserSnapshot(input: Record<string, unknown>): PublicUserEntity {
  const result: PublicUserEntity = {};
  for (const field of PUBLIC_USER_FIELDS) {
    const value = input[field];
    if (value !== undefined) result[field] = value;
  }
  if (result.heroNsfw === undefined && input.coverNsfw !== undefined) {
    result.heroNsfw = input.coverNsfw;
  }
  return result;
}

export function publicUserAliases(
  input: Record<string, unknown>,
  extra: readonly string[] | undefined,
): string[] {
  const candidates = [
    input.userId,
    input.pubkey,
    input.walletAddress,
    typeof input.handle === 'string' ? `@${input.handle}` : undefined,
    ...(Array.isArray(input.wallets)
      ? input.wallets.map((wallet) =>
          wallet !== null && typeof wallet === 'object'
            ? (wallet as Record<string, unknown>).publicKey
            : undefined,
        )
      : []),
    ...(extra ?? []),
  ];
  const aliases: string[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const alias = normalizeAlias(candidate);
    if (alias.length === 0 || seen.has(alias)) continue;
    seen.add(alias);
    aliases.push(alias);
  }
  return aliases;
}

function normalizeAlias(alias: string): string {
  const trimmed = alias.trim();
  if (trimmed.startsWith('@')) return trimmed.slice(1).toLowerCase();
  if (isUserUuid(trimmed)) return trimmed.toLowerCase();
  return trimmed;
}

function isUserUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function readNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : normalizeAlias(trimmed);
}

function repointAliases(queryClient: QueryClient, previousId: string, entityId: string): void {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['users', 'alias'] })) {
    if (query.state.data === previousId) queryClient.setQueryData(query.queryKey, entityId);
  }
}

function setPublicUserAlias(queryClient: QueryClient, alias: string, entityId: string): void {
  const queryKey = publicUserAliasKey(alias);
  if (queryClient.getQueryData(queryKey) === entityId) return;
  queryClient.setQueryData(queryKey, entityId); // ALLOW-STATE-OWNERSHIP: exact public-user alias index
}

function recordDiagnostic(diagnostic: PublicUserDiagnostic): void {
  emit('public-user:diagnostic', diagnostic);
}
